import { supabase } from '../lib/supabase';

export const disputeService = {
  // Create new dispute
  async createDispute(disputeData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('disputes')?.insert({
        filed_by: user?.id,
        negotiation_id: disputeData?.negotiationId,
        transaction_id: disputeData?.transactionId,
        respondent_id: disputeData?.respondentId,
        dispute_category: disputeData?.category,
        priority: disputeData?.priority || 'medium',
        title: disputeData?.title,
        description: disputeData?.description,
        desired_resolution: disputeData?.desiredResolution
      })?.select(`
        *,
        filed_by_profile:user_profiles!disputes_filed_by_fkey(id, full_name, email),
        respondent_profile:user_profiles!disputes_respondent_id_fkey(id, full_name, email),
        mediator_profile:user_profiles!disputes_mediator_id_fkey(id, full_name, email)
      `)?.single();

    if (error) throw error;
    return this.convertToDispute(data);
  },

  // Get all disputes for current user
  async getUserDisputes(filters = {}) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    let query = supabase?.from('disputes')?.select(`
        *,
        filed_by_profile:user_profiles!disputes_filed_by_fkey(id, full_name, email, avatar_url),
        respondent_profile:user_profiles!disputes_respondent_id_fkey(id, full_name, email, avatar_url),
        mediator_profile:user_profiles!disputes_mediator_id_fkey(id, full_name, email, avatar_url)
      `)?.order('filed_at', { ascending: false });

    if (filters?.status) {
      query = query?.eq('dispute_status', filters?.status);
    }
    if (filters?.category) {
      query = query?.eq('dispute_category', filters?.category);
    }
    if (filters?.priority) {
      query = query?.eq('priority', filters?.priority);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data?.map(this.convertToDispute);
  },

  // Get single dispute with full details
  async getDisputeById(disputeId) {
    const { data, error } = await supabase?.from('disputes')?.select(`
        *,
        filed_by_profile:user_profiles!disputes_filed_by_fkey(id, full_name, email, avatar_url),
        respondent_profile:user_profiles!disputes_respondent_id_fkey(id, full_name, email, avatar_url),
        mediator_profile:user_profiles!disputes_mediator_id_fkey(id, full_name, email, avatar_url)
      `)?.eq('id', disputeId)?.single();

    if (error) throw error;
    return this.convertToDispute(data);
  },

  // Update dispute status
  async updateDisputeStatus(disputeId, newStatus, notes = '') {
    const { data, error } = await supabase?.from('disputes')?.update({
        dispute_status: newStatus,
        admin_notes: notes,
        updated_at: new Date()?.toISOString()
      })?.eq('id', disputeId)?.select()?.single();

    if (error) throw error;
    return this.convertToDispute(data);
  },

  // Upload evidence
  async uploadEvidence(disputeId, file, description, evidenceType) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const filePath = `${user?.id}/${disputeId}/${Date.now()}-${file?.name}`;

    const { error: uploadError } = await supabase?.storage?.from('dispute-evidence')?.upload(filePath, file);

    if (uploadError) throw uploadError;

    const { data, error } = await supabase?.from('dispute_evidence')?.insert({
        dispute_id: disputeId,
        uploaded_by: user?.id,
        evidence_type: evidenceType,
        file_path: filePath,
        file_name: file?.name,
        file_size: file?.size,
        mime_type: file?.type,
        description: description
      })?.select()?.single();

    if (error) throw error;
    return this.convertToEvidence(data);
  },

  // Get evidence for dispute
  async getDisputeEvidence(disputeId) {
    const { data, error } = await supabase?.from('dispute_evidence')?.select(`
        *,
        uploader:user_profiles!dispute_evidence_uploaded_by_fkey(id, full_name, avatar_url)
      `)?.eq('dispute_id', disputeId)?.order('uploaded_at', { ascending: false });

    if (error) throw error;

    const evidenceWithUrls = await Promise.all(
      data?.map(async (evidence) => {
        const { data: urlData } = await supabase?.storage?.from('dispute-evidence')?.createSignedUrl(evidence?.file_path, 3600);

        return this.convertToEvidence({
          ...evidence,
          signedUrl: urlData?.signedUrl
        });
      })
    );

    return evidenceWithUrls;
  },

  // Get timeline events
  async getDisputeTimeline(disputeId) {
    const { data, error } = await supabase?.from('dispute_timeline_events')?.select(`
        *,
        actor:user_profiles(id, full_name, avatar_url)
      `)?.eq('dispute_id', disputeId)?.order('created_at', { ascending: true });

    if (error) throw error;
    return data?.map(this.convertToTimelineEvent);
  },

  // Assign mediator
  async assignMediator(disputeId, mediatorId) {
    const { data, error } = await supabase?.from('disputes')?.update({
        mediator_id: mediatorId,
        mediator_assigned_at: new Date()?.toISOString(),
        dispute_status: 'mediator_assigned'
      })?.eq('id', disputeId)?.select()?.single();

    if (error) throw error;

    await supabase?.from('dispute_mediators')?.insert({
        dispute_id: disputeId,
        mediator_id: mediatorId
      });

    return this.convertToDispute(data);
  },

  // Resolve dispute
  async resolveDispute(disputeId, outcome, resolutionDetails) {
    const { data, error } = await supabase?.from('disputes')?.update({
        dispute_status: 'resolved',
        resolution_outcome: outcome,
        resolution_details: resolutionDetails,
        resolved_at: new Date()?.toISOString()
      })?.eq('id', disputeId)?.select()?.single();

    if (error) throw error;
    return this.convertToDispute(data);
  },

  // Escalate dispute
  async escalateDispute(disputeId, escalationReason) {
    const { data, error } = await supabase?.from('disputes')?.update({
        dispute_status: 'escalated',
        priority: 'urgent',
        admin_notes: escalationReason
      })?.eq('id', disputeId)?.select()?.single();

    if (error) throw error;
    return this.convertToDispute(data);
  },

  // Get ALL disputes for admin
  async getAllDisputesAdmin(filters = {}) {
    let query = supabase?.from('disputes')?.select(`
        *,
        filed_by_profile:user_profiles!disputes_filed_by_fkey(id, full_name, email, avatar_url),
        respondent_profile:user_profiles!disputes_respondent_id_fkey(id, full_name, email, avatar_url),
        mediator_profile:user_profiles!disputes_mediator_id_fkey(id, full_name, email, avatar_url)
      `)?.order('filed_at', { ascending: false });

    if (filters?.status) query = query?.eq('dispute_status', filters?.status);
    if (filters?.category) query = query?.eq('dispute_category', filters?.category);
    if (filters?.priority) query = query?.eq('priority', filters?.priority);

    const { data, error } = await query;
    if (error) throw error;
    return data?.map(this.convertToDispute);
  },

  // Send notification to dispute parties
  async sendDisputeNotification(disputeId, userId, title, message) {
    try {
      await supabase?.from('notifications')?.insert({
        user_id: userId,
        title: title,
        message: message,
        notification_type: 'dispute_update',
        is_read: false,
        created_at: new Date()?.toISOString()
      });
    } catch (err) {
      console.error('Error sending dispute notification:', err);
    }
  },

  // Convert database row to camelCase
  convertToDispute(row) {
    return {
      id: row?.id,
      negotiationId: row?.negotiation_id,
      transactionId: row?.transaction_id,
      filedBy: row?.filed_by,
      respondentId: row?.respondent_id,
      mediatorId: row?.mediator_id,
      disputeCategory: row?.dispute_category,
      disputeStatus: row?.dispute_status,
      priority: row?.priority,
      title: row?.title,
      description: row?.description,
      desiredResolution: row?.desired_resolution,
      filedAt: row?.filed_at,
      reviewStartedAt: row?.review_started_at,
      mediatorAssignedAt: row?.mediator_assigned_at,
      resolvedAt: row?.resolved_at,
      closedAt: row?.closed_at,
      resolutionOutcome: row?.resolution_outcome,
      resolutionDetails: row?.resolution_details,
      adminNotes: row?.admin_notes,
      estimatedResolutionDays: row?.estimated_resolution_days,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at,
      filedByProfile: row?.filed_by_profile,
      respondentProfile: row?.respondent_profile,
      mediatorProfile: row?.mediator_profile
    };
  },

  convertToEvidence(row) {
    return {
      id: row?.id,
      disputeId: row?.dispute_id,
      uploadedBy: row?.uploaded_by,
      evidenceType: row?.evidence_type,
      filePath: row?.file_path,
      fileName: row?.file_name,
      fileSize: row?.file_size,
      mimeType: row?.mime_type,
      description: row?.description,
      isVerified: row?.is_verified,
      verifiedBy: row?.verified_by,
      verifiedAt: row?.verified_at,
      uploadedAt: row?.uploaded_at,
      signedUrl: row?.signedUrl,
      uploader: row?.uploader
    };
  },

  convertToTimelineEvent(row) {
    return {
      id: row?.id,
      disputeId: row?.dispute_id,
      actorId: row?.actor_id,
      eventType: row?.event_type,
      eventDescription: row?.event_description,
      oldStatus: row?.old_status,
      newStatus: row?.new_status,
      metadata: row?.metadata,
      createdAt: row?.created_at,
      actor: row?.actor
    };
  }
};